import {
  Button,
  Checkbox,
  Pagination,
  Select,
  Skeleton,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconFileSpreadsheet,
  IconFilter,
  IconPackage,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { mainCategories, subCategories } from './inventory.js';
import { MaterialDetailPanel } from './MaterialDetailPanel.js';
import { ImportDialog, MaterialDialog } from './MaterialDialogs.js';
import classes from './Materials.module.css';
import { PriceHistoryPanel } from './PriceHistoryPanel.js';
import { DEFAULT_MATERIAL_SORT, type MaterialSortState, SortTh, serverSort } from './SortHeader.js';
import type { MaterialsSource } from './source.js';
import type { MaterialCategory, MaterialDetail, MaterialList, MaterialQuery } from './types.js';

const PAGE_SIZE = 10;

interface Filters {
  mainCategory: string | null;
  categoryId: string | null;
  materialType: string | null;
  stockOnly: boolean;
  critical: boolean;
}

const NO_FILTERS: Filters = {
  mainCategory: null,
  categoryId: null,
  materialType: null,
  stockOnly: false,
  critical: false,
};

/** Yeniden yüklenirken önceki liste ekranda kalır (`previous`); ilk yüklemede iskelet (M6f G5-09). */
type ListState =
  | { kind: 'loading'; previous: MaterialList | null }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: MaterialList };

export interface MaterialMasterScreenProps {
  source: MaterialsSource;
}

/**
 * Malzeme ana listesi (`satin-alma-akisi.jpeg` "Material Master" ve "Material Detail"; ADR 0026
 * §1, §4): arama, Category / Sub Category / Material Type süzgeçleri, "Only Stock Items",
 * "Critical Items", Material / UOM / Type tablosu ve sayfalama; seçilen kalemin kartı. Katalog
 * ofisin verisidir: ofiste kalem açılır, düzeltilir ve Excel'den içe aktarılır; gemide salt
 * okunur.
 */
export function MaterialMasterScreen({ source }: MaterialMasterScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) => t(`materials.${key}`, options);
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [vessels, setVessels] = useState<PersonnelVessel[]>([]);
  const [text, setText] = useState('');
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [query, setQuery] = useState<MaterialQuery>({ page: 1, pageSize: PAGE_SIZE });
  // Sıra sunucuda (M6f G5-15): sayfalı veride yalnız görünen sayfa sıralanmaz
  const [sort, setSort] = useState<MaterialSortState>(DEFAULT_MATERIAL_SORT);
  const [state, setState] = useState<ListState>({ kind: 'loading', previous: null });
  // Dar ekranda süzgeç kartı kapalı başlar: liste önde (docs/ekranlar/README.md "Responsive")
  const [filtersOpen, setFiltersOpen] = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MaterialDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MaterialDetail | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);
  // Kullanıcı satır seçince kart görünür alana gelir; ilk yüklemedeki otomatik seçimde kaymaz (G5-03)
  const reveal = useRef(false);

  useEffect(() => {
    Promise.all([source.categories(), source.scope === 'web' ? source.vessels() : []])
      .then(([loaded, list]) => {
        setCategories(loaded);
        setVessels(list);
      })
      .catch(() => setCategories([]));
  }, [source]);

  const load = useCallback(async () => {
    setState((current) => ({
      kind: 'loading',
      previous:
        current.kind === 'ready'
          ? current.list
          : current.kind === 'loading'
            ? current.previous
            : null,
    }));
    try {
      const list = await source.materials({
        ...query,
        ...serverSort(sort, DEFAULT_MATERIAL_SORT),
      });
      setState({ kind: 'ready', list });
      setSelectedId((current) =>
        current && list.items.some((item) => item.id === current)
          ? current
          : (list.items[0]?.id ?? null),
      );
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, query, sort]);
  useEffect(() => {
    void load();
  }, [load]);

  /** Sıra değişince ilk sayfaya dönülür; süzgeçler korunur. */
  const changeSort = (next: MaterialSortState) => {
    setSort(next);
    setQuery((current) => ({ ...current, page: 1 }));
  };

  const loadDetail = useCallback(
    async (id: string) => {
      setDetailError(null);
      try {
        setDetail(await source.material(id));
      } catch (error) {
        setDetail(null);
        setDetailError(errorCode(error));
      }
    },
    [source],
  );
  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  /** Kartı görünür alana getirir; "hareketi azalt" tercihinde kaydırma animasyonsuz. */
  const revealDetail = useCallback(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    detailRef.current?.scrollIntoView?.({
      block: 'nearest',
      behavior: reduce ? 'auto' : 'smooth',
    });
  }, []);

  useEffect(() => {
    if (!detail || !reveal.current) return;
    reveal.current = false;
    revealDetail();
  }, [detail, revealDetail]);

  const select = (id: string) => {
    if (id === selectedId && detail) {
      revealDetail();
      return;
    }
    reveal.current = true;
    setSelectedId(id);
  };

  const mains = useMemo(() => mainCategories(categories), [categories]);
  const subs = useMemo(() => subCategories(categories, draft.mainCategory), [categories, draft]);

  const search = (filters: Filters = draft, q: string = text) =>
    setQuery({
      page: 1,
      pageSize: PAGE_SIZE,
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(filters.mainCategory ? { mainCategory: filters.mainCategory } : {}),
      ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
      ...(filters.materialType ? { materialType: filters.materialType } : {}),
      ...(filters.stockOnly ? { stockOnly: true } : {}),
      ...(filters.critical ? { critical: true } : {}),
    });
  const reset = () => {
    setDraft(NO_FILTERS);
    setText('');
    search(NO_FILTERS, '');
  };

  const list =
    state.kind === 'ready' ? state.list : state.kind === 'loading' ? state.previous : null;
  const busy = state.kind === 'loading';
  const filtered = Boolean(
    query.q ||
      query.mainCategory ||
      query.categoryId ||
      query.materialType ||
      query.stockOnly ||
      query.critical,
  );
  const pages = list ? Math.max(1, Math.ceil(list.total / list.pageSize)) : 1;
  const all = (key: string) => ({ value: '', label: k(key) });

  return (
    <div className={classes.screen}>
      {/* Açıklama satırı yok: 1280×720'de başlık, on satır ve sayfalayıcı sığar (G5-03) */}
      <PageHeader
        title={k('title')}
        icon={<IconPackage stroke={1.6} />}
        crumb={k('search')}
        actions={
          source.catalog ? (
            <>
              <Button
                variant="default"
                leftSection={<IconFileSpreadsheet size={16} />}
                onClick={() => setImporting(true)}
              >
                {k('import.open')}
              </Button>
              <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
                {k('new')}
              </Button>
            </>
          ) : null
        }
      />

      <form
        className={classes.searchBar}
        aria-label={k('searchPlaceholder')}
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        <TextInput
          className={classes.searchInput}
          leftSection={<IconSearch size={16} aria-hidden />}
          placeholder={k('searchPlaceholder')}
          aria-label={k('searchPlaceholder')}
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
        />
        <Button
          variant="default"
          leftSection={<IconFilter size={16} />}
          aria-expanded={filtersOpen}
          aria-controls="material-filters"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          {k('filter')}
        </Button>
      </form>

      <div className={classes.masterGrid} data-filters={filtersOpen || undefined}>
        {filtersOpen ? (
          <section id="material-filters" className={classes.filterCard} aria-label={k('filters')}>
            <Select
              label={k('category')}
              data={[all('allCategories'), ...mains.map((value) => ({ value, label: value }))]}
              value={draft.mainCategory ?? ''}
              allowDeselect={false}
              onChange={(value) =>
                setDraft((d) => ({ ...d, mainCategory: value || null, categoryId: null }))
              }
            />
            <Select
              label={k('subCategory')}
              data={[
                all('allSubCategories'),
                ...subs.map((c) => ({
                  value: c.id,
                  label: draft.mainCategory
                    ? c.subCategory
                    : `${c.subCategory} · ${c.mainCategory}`,
                })),
              ]}
              value={draft.categoryId ?? ''}
              allowDeselect={false}
              searchable
              onChange={(value) => setDraft((d) => ({ ...d, categoryId: value || null }))}
            />
            <Select
              label={k('materialType')}
              data={[
                all('allTypes'),
                ...(['Consumable', 'Spare Part', 'Equipment/Spare', 'Provision'] as const).map(
                  (value) => ({
                    value,
                    label: t(`materials.types.${value}`),
                  }),
                ),
              ]}
              value={draft.materialType ?? ''}
              allowDeselect={false}
              onChange={(value) => setDraft((d) => ({ ...d, materialType: value || null }))}
            />
            <div className={classes.checks}>
              <Checkbox
                label={k('onlyStock')}
                checked={draft.stockOnly}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setDraft((d) => ({ ...d, stockOnly: checked }));
                }}
              />
              <Checkbox
                label={k('criticalOnly')}
                checked={draft.critical}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setDraft((d) => ({ ...d, critical: checked }));
                }}
              />
            </div>
            <div className={classes.filterButtons}>
              <Button variant="light" color="gray" onClick={reset}>
                {k('reset')}
              </Button>
              <Button onClick={() => search()}>{k('searchButton')}</Button>
            </div>
          </section>
        ) : null}

        <div className={classes.listCard} aria-busy={busy}>
          {state.kind === 'error' ? (
            <EmptyState
              tone="danger"
              title={k('error')}
              action={
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => void load()}
                >
                  {k('retry')}
                </Button>
              }
            />
          ) : list === null ? (
            <TableSkeleton columns={3} rows={PAGE_SIZE} />
          ) : list.total === 0 ? (
            <EmptyState
              icon={
                filtered ? (
                  <IconSearch size={22} stroke={1.8} />
                ) : (
                  <IconPackage size={22} stroke={1.8} />
                )
              }
              title={filtered ? k('noMatch') : k('emptyTitle')}
              description={
                filtered ? undefined : source.catalog ? k('emptyOffice') : k('emptyShip')
              }
              action={
                filtered ? (
                  <Button size="xs" variant="default" onClick={reset}>
                    {t('common.clearFilters')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <section
                className={classes.tableWrap}
                aria-label={k('table')}
                data-busy={busy || undefined}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={classes.table} data-variant="master">
                  <colgroup>
                    <col className={classes.colMaterial} />
                    <col className={classes.colUom} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <SortTh
                        column="name"
                        label={k('columns.material')}
                        sort={sort}
                        onSort={changeSort}
                      />
                      <SortTh
                        column="unit"
                        label={k('columns.uom')}
                        sort={sort}
                        onSort={changeSort}
                      />
                      <SortTh
                        column="type"
                        label={k('columns.type')}
                        sort={sort}
                        onSort={changeSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {list.items.map((item) => (
                      // Bütün satır tıklanır; klavye için ad hücresindeki düğme (G5-08)
                      <tr
                        key={item.id}
                        aria-selected={item.id === selectedId}
                        data-clickable
                        onClick={() => select(item.id)}
                      >
                        <td>
                          <UnstyledButton
                            className={classes.rowButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              select(item.id);
                            }}
                            aria-pressed={item.id === selectedId}
                          >
                            <span className={classes.itemName}>{item.name}</span>
                            {item.specification ? (
                              <span className={classes.spec}>{` · ${item.specification}`}</span>
                            ) : null}
                          </UnstyledButton>
                        </td>
                        <td>{item.unit}</td>
                        <td className={classes.secondary}>
                          {t(`materials.types.${item.materialType}`, {
                            defaultValue: item.materialType,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <div className={classes.pager}>
                <span>
                  {k('showing', {
                    from: (list.page - 1) * list.pageSize + 1,
                    to: Math.min(list.page * list.pageSize, list.total),
                    total: list.total,
                  })}
                </span>
                {pages > 1 ? (
                  <Pagination
                    total={pages}
                    value={list.page}
                    onChange={(page) => setQuery((q) => ({ ...q, page }))}
                    siblings={1}
                    boundaries={1}
                    aria-label={k('pagination')}
                    getControlProps={(control) => ({ 'aria-label': k(`page.${control}`) })}
                    getItemProps={(page) => ({ 'aria-label': k('page.number', { page }) })}
                  />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      <div ref={detailRef} className={classes.detailAnchor}>
        {detailError ? (
          <Panel title={k('detail.title')}>
            <EmptyState
              tone="danger"
              title={k('detail.error')}
              action={
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => {
                    if (selectedId) void loadDetail(selectedId);
                  }}
                >
                  {k('retry')}
                </Button>
              }
            />
          </Panel>
        ) : detail ? (
          <div className={classes.detailStack}>
            <MaterialDetailPanel
              material={detail}
              scope={source.scope}
              vessels={vessels}
              actions={
                source.catalog ? (
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPencil size={14} />}
                    onClick={() => setEditing(detail)}
                  >
                    {k('edit')}
                  </Button>
                ) : null
              }
            />
            {/* Fiyat geçmişi (ortağın notu #13; ADR 0047 §5) */}
            <PriceHistoryPanel rows={detail.priceHistory ?? []} scope={source.scope} />
          </div>
        ) : list && list.total > 0 ? (
          <Skeleton height={240} radius="md" />
        ) : null}
      </div>
      {source.catalog ? (
        <>
          <MaterialDialog
            opened={editing !== null}
            material={editing === 'new' ? null : editing}
            categories={categories}
            catalog={source.catalog}
            onClose={() => setEditing(null)}
            onSaved={(saved) => {
              setEditing(null);
              setSelectedId(saved.id);
              setDetail(saved);
              void load();
            }}
          />
          <ImportDialog
            opened={importing}
            catalog={source.catalog}
            onClose={() => setImporting(false)}
            onImported={() => {
              void source.categories().then(setCategories);
              void load();
            }}
          />
        </>
      ) : null}
    </div>
  );
}

/** Tablo biçimli iskelet: başlık bandı ve 44 px satırlar (M6f G5-09, G5-27). */
export function TableSkeleton({ columns, rows }: { columns: number; rows: number }) {
  return (
    <div className={classes.skeleton} aria-hidden>
      <div className={classes.skeletonHead}>
        {Array.from({ length: columns }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: sabit sayıda yer tutucu
          <Skeleton key={index} height={10} width="40%" radius="sm" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: sabit sayıda yer tutucu
        <div key={row} className={classes.skeletonRow}>
          {Array.from({ length: columns }, (_, index) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: sabit sayıda yer tutucu
              key={index}
              height={10}
              width={index === 0 ? '70%' : '45%'}
              radius="sm"
            />
          ))}
        </div>
      ))}
    </div>
  );
}
